import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { CountryGroupItem, Discount, PaymentSummary, PropertyDescription, RatePlan, RoomTypeConfig, RoomTypeRoomItem, RoomTypeRoomItemRoom, UtilitiesPayment } from '../../core/api/api.models';
import { SearchAvailabilityQuery } from '../../core/models/booking-model';
import { BtrBookingService } from '../../core/services/btr-booking.service';
import { format_date } from '../../utilities/date-utils';
import { paymentConfigInfo } from '../../Contansts';
import { DomSanitizer } from '@angular/platform-browser';
import { PlatformService } from '../../core/platform.service';

const MIN_WIDTH_ALLOW_SCROLL = 960;
const DISTANCE_START_SCROLL = 175;
const STEP_SCROLL = 10;

@Component({
  selector: 'app-booking-summary',
  standalone: false,
  templateUrl: './booking-summary.component.html',
  styleUrls: ['./booking-summary.component.sass'],
  encapsulation: ViewEncapsulation.None,
})
export class BookingSummaryComponent {
  @Input('roomType') roomType?: RoomTypeRoomItem;
  @Input('room') room?: RoomTypeRoomItemRoom;
  @Input('hotels') hotels?: CountryGroupItem[];
  @Input('ratePlan') ratePlan?: RatePlan;
  @Input('paymentSummary') paymentSummary: PaymentSummary | undefined;
  @Input('searchAvailabilityQuery') searchAvailabilityQuery?: SearchAvailabilityQuery | undefined | null;
  @Input('propertyName') propertyName?: string;
  @Input('roomTypeConfig') roomTypeConfig?: RoomTypeConfig
  @Input('discounts') discounts: Discount[] = []

  @Output('onSubmit') onSubmit = new EventEmitter();
  @Input('heroImage') heroImage?: string;
  @Input('atExtraPage') atExtraPage?: boolean = false;

  depositToolTip?: PropertyDescription;
  advRentToolTip?: PropertyDescription;
  holdingFeeToolTip?: PropertyDescription;
  bondToolTip?: PropertyDescription;

  loadDescription: boolean = false;

  paymentConfigInfo = paymentConfigInfo;

  tooltipEvent: string = 'hover';

  panelState = {
    monthlyRatePackageActive: false,
    advanceRentActive: false,
    securityDepositActive: false,
    adminFeeActive: false,
    promoCode: false,
    dicountCodes: false,
    addOns: false,
    utilities: false,
    securityBondActive: false,
    holdingFeeActive: false,
    monthlyAddons: false,
    discountCode: false,
    bondInfoPayment: false,
  }

  constructor(
    private btrBookingService: BtrBookingService,
    private sanitizer: DomSanitizer,
    private platform: PlatformService,

  ) { }

  ngOnInit() {    
    this.tooltipEvent = this.platform.isMobile() ? 'focus' : 'hover';
    this.setDynamicHeight();
  }

  getDescription(type: string) {
    let description = '';
    if (!this.roomTypeConfig || !this.roomTypeConfig.propertyDescriptions.length) {
      return '';
    }
    if (!this.loadDescription) {
      const { propertyDescriptions } = this.roomTypeConfig;
      this.depositToolTip = propertyDescriptions.find(item => item.referenceCode == paymentConfigInfo.depositToolTip);
      this.advRentToolTip = propertyDescriptions.find(item => item.referenceCode == paymentConfigInfo.advRentToolTip);
      this.holdingFeeToolTip = propertyDescriptions.find(item => item.referenceCode == paymentConfigInfo.holdingFeeToolTip);
      this.bondToolTip = propertyDescriptions.find(item => item.referenceCode == paymentConfigInfo.bondToolTip);
      this.loadDescription = true;
    }

    switch (type) {
      case paymentConfigInfo.depositToolTip:
        description = this.depositToolTip ? this.depositToolTip.description : '';
        break;
      case paymentConfigInfo.holdingFeeToolTip:
        description = this.holdingFeeToolTip ? this.holdingFeeToolTip.description : '';
        break;
      case paymentConfigInfo.advRentToolTip:
        description = this.advRentToolTip ? this.advRentToolTip.description : '';
        break;
      case paymentConfigInfo.bondToolTip:
        description = this.bondToolTip ? this.bondToolTip.description : '';
        break;
    }

    return description ? this.sanitizer.bypassSecurityTrustHtml(description) : '';
  }

  submit() {
    this.onSubmit.next(null);
  }

  formatDate(date: Date | undefined | string) {
    if (date) {
      const dateFormat = new Date(date)
      return format_date(dateFormat, 'DD/MM/YYYY')
    }
    return '';
  }

  formatNumber(input: number) {
    return input.toFixed(2).toString();
  }

  canShowUtilities() {
    if (this.paymentSummary?.utilities.length) return true;
    return false;
  }

  canShowExtras() {
    if (this.paymentSummary?.addOns.length) return true;
    return false;
  }

  getTotalMonthly() {
    if (this.paymentSummary){
      let totalMonthlyUtilities = 0, totalMonthAddons = 0;
      if(this.paymentSummary.utilities && this.paymentSummary.utilities.length){
        totalMonthlyUtilities = this.paymentSummary.utilities.reduce((a, b) => a + b.monthlyRateAmount * b.quantity, 0);
      }
      if(this.paymentSummary.addOns && this.paymentSummary.addOns.length){
        totalMonthAddons = this.paymentSummary.addOns.reduce((a, b) => a + b.monthlyRateAmount * b.quantity, 0);
      }

      return this.paymentSummary.rate.monthlyRateAmount + totalMonthlyUtilities + totalMonthAddons;
    }

    return 0;
  }

  getPropertyName(propertyCode: string | undefined) {
    if (propertyCode) {
      return this.hotels?.find(hotel => hotel.hotelRefID == propertyCode)?.hotelName || '';
    }
    return '';
  }

  getImage(){
    return this.heroImage ? this.heroImage : '';
  }

  getTotalExtra(utilities: UtilitiesPayment[], fullMonth: boolean = true){
    if(fullMonth){
      return utilities.reduce((sum, uti) => sum + (uti.monthlyRateAmount * uti.quantity), 0);
    } else {
      return utilities.reduce((sum, uti) => sum + (uti.quantity * uti.totalAmount), 0);
    }
  }

  monthsBetweenDates(date1: Date | string, date2: Date | string): number {
    // // Ensure date1 is earlier than date2
    date1 = new Date(date1);
    date2 =  new Date(date2);
    
    if (date1 > date2) {
      [date1, date2] = [date2, date1];
    }

    let months;
    months = (date2.getFullYear() - date1.getFullYear()) * 12;
    months -= date1.getMonth();
    months += date2.getMonth();
    return months <= 0 ? 0 : (months + (date1.getDate() == 1 ? 1 : 0));
  }

  canShowBondSection(){
    if(!this.paymentSummary){
      return false;
    }

    if(this.paymentSummary.bondAmount && this.paymentSummary.guaranteePolicy.merchantCollectBondOnBehalf){
      return true;
    }

    return false;
  }

  canShowBondInfo(){
    if(!this.paymentSummary){
      return false;
    }

    if(this.paymentSummary.actualBondAmount > 0 && !this.paymentSummary.guaranteePolicy.merchantCollectBondOnBehalf){
      return true;
    }

    return false;
  }

  dynamicHeight: string = '';
  dynamicTop: number = 0;  // Initial top position of the div
  initialOffsetTop: number = 0;  // To store the initial position
  lastScrollTop: number = 0;

  @HostListener('window:resize', [])
  onWindowResize() {
    this.setDynamicHeight();
    this.checkOverflow();
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    if (window.innerWidth >= MIN_WIDTH_ALLOW_SCROLL) {
      // Check if the user is scrolling down or up
      if (scrollTop > this.lastScrollTop && scrollTop > DISTANCE_START_SCROLL) {
        // Scrolling down
        if(this.checkOverflow()) {
          this.dynamicTop = this.dynamicTop + STEP_SCROLL; 
        }    
        this.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
      } else {
        // Scrolling up
        this.dynamicTop = scrollTop <= DISTANCE_START_SCROLL ? 0 : (this.dynamicTop - STEP_SCROLL <= 0 ? 0 : this.dynamicTop - STEP_SCROLL);  // Decrease top position
        this.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop;
      }
      
      if (this.dynamicTop < 0) {
        this.dynamicTop = 0;
      }
    } else {
      this.dynamicTop = 0;
    }
  }

  setDynamicHeight() {
    this.dynamicHeight = `calc(100vh - 120px)`;
  }

  ngAfterViewInit() {
    this.checkOverflow();
  }

  @ViewChild('myDiv') myDiv!: ElementRef;
  checkOverflow() {
    const element = this.myDiv.nativeElement;
    
    // Check vertical and horizontal overflow
    const isOverflowingVertically = element.scrollHeight > element.clientHeight;
    const isOverflowingHorizontally = element.scrollWidth > element.clientWidth;
    return isOverflowingVertically || isOverflowingHorizontally;
  }

  getTaxDes(utility: UtilitiesPayment) {
    const taxFee = utility.taxFee;
    if(!taxFee) return '';

    if(taxFee.incTaxFeeItems.length && taxFee.excTaxFeeItems.length) return '';

    if(taxFee.incTaxFeeItems.length) return `${taxFee.incTaxFeeItems[0].percent}% GST Inclusive`;

    if(taxFee.excTaxFeeItems.length) return `${taxFee.excTaxFeeItems[0].percent}% GST Exclusive`;

    return '';
  }



}
